import React from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next'; // nuevo
import { RenderNestedComponent } from '../../../types';

const EXPAND_COL_WIDTH = 28;

const RenderNestedTable: RenderNestedComponent = (
  row,
  columns,
  hiddenColumns,
  colSizeMap,
  childMap
) => {
  const LABEL_COLUMN_ACCESSOR = childMap.labelColumnAccessor;
  const DETAIL_ROWS = childMap.childRows;
  const cMap = childMap.map;
  const theme = useTheme();
  const { t } = useTranslation(); // nuevo
  // eslint-disable-next-line react/destructuring-assignment
  const visibleCols = columns.filter(({ id }) => id && !hiddenColumns.has(id as string));

  const colWidthMap: Record<string, number | undefined> = {};
  visibleCols.forEach(col => {
    const id = col.id as string;
    colWidthMap[id] = colSizeMap?.[id] ?? (col.size || col.minSize || undefined);
  });

  const expandWidth = colSizeMap?.['mrt-row-expand'] ?? EXPAND_COL_WIDTH;

  return (
    <Box sx={{ p: 0, backgroundColor: t => t.palette.background.paper }}>
      <TableContainer sx={{ overflow: 'hidden' }}>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableBody>
            {DETAIL_ROWS.map(detailRowKey => (
              <TableRow key={detailRowKey}>
                <TableCell
                  key="__expand_spacer__"
                  sx={{ borderBottom: 'none', p: 0, width: expandWidth, boxSizing: 'border-box' }}
                />
                {visibleCols.map(col => {
                  const colId = col.id as string;
                  const isLabelCol = colId === LABEL_COLUMN_ACCESSOR;
                  let content: React.ReactNode = null;
                  let align: 'left' | 'center' | 'right' = 'left';

                  if (isLabelCol) {
                    // content = detailRowKey; anterior
                    content = t(detailRowKey); // nuevo
                    align = 'right';
                  } else if (cMap[colId]) {
                    const defs = cMap[colId];
                    const match = defs.find(({ rowKey }) => rowKey === detailRowKey);
                    if (match) {
                      const { original } = row;
                      const raw = original[match.accessor as keyof typeof original] ?? '';
                      let valueStr: string;
                      if (typeof raw === 'number') {
                        valueStr = raw.toFixed(2).replace(/\.00$/, '');
                      } else if (!Number.isNaN(Number(raw)) && String(raw).length < 20) {
                        const num = Number(raw);
                        valueStr = num.toFixed(2).replace(/\.00$/, '');
                      } else {
                        valueStr = raw as string;
                      }
                      let finalValue = valueStr;
                      if (match.prefix) finalValue = `${match.prefix}${finalValue}`;
                      if (match.suffix) finalValue = `${finalValue}${match.suffix}`;
                      content = match.renderCell ? match.renderCell(finalValue) : finalValue;
                    }
                  }

                  const width = colWidthMap[colId];

                  return (
                    <TableCell
                      key={colId}
                      data-col-id={colId}
                      align={isLabelCol ? 'right' : 'left'}
                      sx={{
                        borderBottom: 'none',
                        p: 0,
                        ...(width !== undefined ? { width } : {}),
                        fontSize: theme.custom?.tableFontSizePx || '12px',
                        boxSizing: 'border-box',

                        ...(isLabelCol && {
                          fontWeight: 700,
                          pr: { lg: 1.3, xl: 6 },
                          whiteSpace: 'nowrap',
                          textAlign: 'right !important',
                        }),
                      }}
                    >
                      {content || null}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default RenderNestedTable;
